/**
 * Types + DTOs + Zod-schemas voor de impersonation-module.
 *
 * Spec: docs/modules/impersonation.md.
 *
 * Read-only impersonation tool in backoffice. HR/admin/teamleider kan
 * "view-as employee" om te zien wat een monteur ziet op zijn portal-mobile.
 * Hulpmiddel voor support en debug.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Hard limit voor de duur van een impersonation-sessie. Na deze tijd is
 * de sessie automatisch verlopen — onafhankelijk van of `beeindigdOp`
 * handmatig is gezet of niet. Server-side enforcement via `eindTijd`.
 */
export const IMPERSONATION_MAX_MINUTES = 60

/**
 * HTTP-header die op portal-requests een impersonation-sessie aankondigt.
 * De waarde is het bearer-token uit `ImpersonationSessie.token`.
 */
export const IMPERSONATION_HEADER = 'x-impersonation-token'

/**
 * Cookie-naam die in de portal-app door middleware gelezen wordt om de banner
 * te tonen + ingelezen wordt door server-helpers om de header te zetten.
 */
export const IMPERSONATION_COOKIE = 'streep_imp_token'

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface ImpersonationActorDTO {
  id: string
  name: string | null
  email: string
}

export interface ImpersonationSessieDTO {
  id: string
  adminUser: ImpersonationActorDTO
  targetUser: ImpersonationActorDTO
  reden: string
  startTijd: string
  eindTijd: string
  beeindigdOp: string | null
  /** Aantal geregistreerde GET-acties tijdens deze sessie. */
  aantalActies: number
  /** True als sessie nog actief is (eindTijd in toekomst en beeindigdOp null). */
  isActief: boolean
}

export interface ImpersonationStartResponseDTO {
  sessieId: string
  token: string
  eindTijd: string
}

export interface ImpersonationActieDTO {
  id: string
  pad: string
  methode: string
  createdAt: string
}

export interface ImpersonationSessieDetailDTO extends ImpersonationSessieDTO {
  acties: ImpersonationActieDTO[]
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

export const startImpersonationSchema = z.object({
  targetUserId: idParam(),
  reden: z.string().trim().min(3, 'Geef een korte reden op (≥3 tekens)').max(500),
})

export type StartImpersonationInput = z.infer<typeof startImpersonationSchema>

export const auditQuerySchema = z.object({
  adminUserId: idParam().optional(),
  targetUserId: idParam().optional(),
  vanaf: z.string().datetime().optional(),
  totEnMet: z.string().datetime().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
  sortField: z
    .enum(['startTijd', 'admin', 'target', 'reden', 'acties'])
    .optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
})

export type AuditQueryInput = z.infer<typeof auditQuerySchema>
